import SEO from '../../components/SEO';
import React from 'react';
import { Pill } from 'lucide-react';
import FAQRichSnippet from '../../components/FAQRichSnippet';

import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';

const MED_MGMT_FAQS = [
  { q: "What is medication management?", a: "Medication management involves working with a psychiatric provider to evaluate, prescribe, and monitor medications for mental health conditions like depression, anxiety, bipolar disorder, ADHD, and schizophrenia." },
  { q: "How often will I have medication check-ins?", a: "Initially, check-ins may be every 2 to 4 weeks as your provider adjusts dosage or monitors side effects. Once stable, visits may be monthly or quarterly." },
  { q: "Can I do medication management without therapy?", a: "Yes, medication management can be a standalone service. However, many patients benefit from combining medication with therapy for the best outcomes." },
  { q: "Is medication management available via telehealth?", a: "Yes. SDORA Wellbeing offers medication management appointments via secure telehealth for eligible patients in Maryland." },
];

const MedicationManagementPage = () => {
  return (
    <div className="min-h-screen bg-white">
      
      <Navigation />
      <SEO title="Medication Management" description="Psychiatric medication management services at SDORA Wellbeing in Pikesville, MD. Medication evaluation, monitoring, and coordination with your treatment team. Telehealth available. Accepting Medicaid." image="https://static.prod-images.emergentagent.com/jobs/01501ca6-ddde-43b6-bfc5-0444b2f0bae0/images/b78e14aabe322fe7a97fec9a43ed171b1f4c4d6e4296cd87814483d5c213a29a.jpeg" path="/services/medication-management" />
      
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <Pill className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Medication Management
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl">
            Our licensed clinicians carefully manage medications based on assessed needs using evidence-based practices.
          </p>
        </div>
      </div>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Approach</h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Our Experts Medication Management Provider</h3>
                    <p className="text-gray-600">Board-certified providers specializing in psychiatric medication.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Regular Monitoring</h3>
                    <p className="text-gray-600">Ongoing assessment to ensure optimal effectiveness and safety.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Personalized Plans</h3>
                    <p className="text-gray-600">Tailored medication strategies based on your unique needs.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                  <div>
                    <h3 className="font-semibold text-lg">Safety First</h3>
                    <p className="text-gray-600">Careful monitoring for side effects and drug interactions.</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-purple-50 p-8 rounded-xl">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What to Expect</h2>
              <p className="text-gray-700 mb-4">Our medication management services include:</p>
              <ul className="space-y-2 text-gray-700">
                <li>• Initial psychiatric evaluation</li>
                <li>• Medication prescription and adjustments</li>
                <li>• Regular follow-up appointments</li>
                <li>• Side effect management</li>
                <li>• Coordination with therapy providers</li>
                <li>• Education about medications</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <FAQRichSnippet faqs={MED_MGMT_FAQS} />
      </div>
      <Footer />
    </div>
  );
};

export default MedicationManagementPage;